<script setup lang="ts" generic="TValue, T extends PejerTablesList">
import type { AccessorKeyColumnDef, ColumnDef } from '@tanstack/vue-table'
import { Plus, Search } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import type { Groups } from '../../../tables'
import { PejerTablesList } from '../../../types/base'
import Badge from '../../ui/badge/Badge.vue'

const props = defineProps<{
  columns: AccessorKeyColumnDef<T, TValue>[]
  visibleColumns: Record<string, boolean>
  groupColors?: Groups
  unsearchableColumns?: string[]
}>()

const emit = defineEmits<{
  (e: 'triggerSearch', columnId: string): void
  (e: 'resetDisplayedColumns'): void
  (e: 'showColumn', columnId: string): void
}>()

const showMoreColumns = ref(false)

function toggleShowMoreColumns() {
  showMoreColumns.value = !showMoreColumns.value
}

function resetDisplayedColumns() {
  emit('resetDisplayedColumns')
}

function showColumn(columnId: string) {
  emit('showColumn', columnId)
}

function triggerSearch(columnId: string) {
  emit('triggerSearch', columnId)
}

function shouldHideColumn(columnId: string) {
  return props.unsearchableColumns?.includes(columnId)
}

const columnGroups = computed(() => {
  const groups = new Map<string, ColumnDef<T, TValue>[]>()
  props.columns.forEach((col) => {
    const group = col.meta?.group ?? '_'
    if (!groups.has(group)) {
      groups.set(group, [])
    }
    groups.get(group)?.push(col)
  })
  // console.log(groups)
  return groups
})

const hiddenColumns = computed(() => {
  const hiddenMap: Record<string, AccessorKeyColumnDef<T, TValue>[]> = {}
  props.columns.forEach((col) => {
    const colId = (col.id || (col.accessorKey as string)).replace('.', '_')
    if (!props.visibleColumns[colId]) {
      const group = col.meta?.group ?? '_'
      if (!(group in hiddenMap)) {
        hiddenMap[group] = []
      }
      hiddenMap[group].push(col)
    }
  })
  return hiddenMap
})
</script>

<template>
  <div class="flex gap-2 justify-between">
    <div
      class="mb-1 flex gap-2 flex-wrap overflow-hidden transition-all duration-200 ease-in-out w-full"
      :class="{
        'h-7': !showMoreColumns,
        'h-auto': showMoreColumns,
      }"
    >
      <template v-for="[group] of columnGroups" :key="group">
        <div
          v-if="hiddenColumns[group] && hiddenColumns[group].length"
          class="flex rounded-md text-white p-1"
          :class="{
            'border flex-col': showMoreColumns,
          }"
        >
          <div
            class="text-xs font-semibold p-1 mr-1 text-nowrap flex"
            v-if="group !== '_'"
            :style="{
              color: groupColors?.[group]?.color.accent || 'gray',
            }"
          >
            <component
              v-if="groupColors?.[group]?.icon && showMoreColumns"
              class="w-4 h-4 mr-1 shrink-0"
              :is="groupColors?.[group]?.icon"
            ></component>
            {{ group }}:
          </div>
          <div class="flex flex-wrap gap-1">
            <Badge
              v-for="column in hiddenColumns[group]"
              :key="(column as any).id || (column as any).accessorKey"
              class="cursor-pointer gap-1 hover:bg-gray-50 flex-shrink-0 group p-0 bg-white"
              variant="outline"
            >
              <div
                class="px-1 flex"
                @click="showColumn((column as any).id || (column as any).accessorKey)"
              >
                <Plus
                  :size="14"
                  class="mr-3"
                  :style="{
                    color: groupColors?.[group]?.color.accent || 'gray',
                  }"
                />
                {{ column.header }}
              </div>
              <div
                v-if="!shouldHideColumn((column as any).id || (column as any).accessorKey)"
                class="group-hover:opacity-100 opacity-0 transition-all hover:bg-blue-500 hover:text-white p-[.25rem] rounded-sm"
                @click="triggerSearch((column as any).id || (column as any).accessorKey)"
              >
                <Search :size="14" />
              </div>
            </Badge>
          </div>
        </div>
      </template>
    </div>
    <div class="flex gap-2 flex-shrink-0 pt-1">
      <Badge
        class="text-xs flex-shrink-0 cursor-pointer rounded-md flex items-center justify-center h-6 hover:bg-gray-50 transition-all duration-200 ease-in-out"
        @click="toggleShowMoreColumns"
        variant="secondary"
      >
        {{ showMoreColumns ? 'Hide' : 'Show' }} more columns
      </Badge>
      <Badge
        class="text-xs flex-shrink-0 cursor-pointer rounded-md flex items-center justify-center h-6 hover:bg-gray-50 transition-all duration-200 ease-in-out"
        @click="resetDisplayedColumns"
        variant="secondary"
      >
        Reset displayed columns
      </Badge>
    </div>
  </div>
</template>
